import { z } from "zod";
import type { ChampionId, EntityId, WireSimState } from "@pit/protocol";

export const MELEE_ATTACK_ANIMATION_MS = 650;

const ABILITY_SCHEMA = z.object({
  id: z.string(),
  targetType: z.string(),
  rangeUnits: z.number().int().default(0),
  delivery: z.object({ kind: z.string() }).optional(),
});
const CHAMPION_SCHEMA = z.object({ id: z.string(), abilities: z.array(z.string()) });

function loadMeleeSlots(): ReadonlyMap<string, ReadonlySet<number>> {
  const abilityFiles = import.meta.glob<string>(
    "../../../../packages/content/data/abilities/*.json",
    { eager: true, query: "?raw", import: "default" },
  );
  const abilities = new Map<string, z.infer<typeof ABILITY_SCHEMA>>();
  for (const raw of Object.values(abilityFiles)) {
    const ability = ABILITY_SCHEMA.parse(JSON.parse(raw) as unknown);
    abilities.set(ability.id, ability);
  }
  const championFiles = import.meta.glob<string>(
    "../../../../packages/content/data/champions/*.json",
    { eager: true, query: "?raw", import: "default" },
  );
  return new Map(
    Object.values(championFiles).map((raw) => {
      const champion = CHAMPION_SCHEMA.parse(JSON.parse(raw) as unknown);
      const slots = new Set<number>();
      champion.abilities.forEach((id, slot) => {
        const ability = abilities.get(id);
        if (
          ability?.targetType === "enemy" &&
          ability.rangeUnits <= 200 &&
          ability.delivery?.kind !== "projectile"
        )
          slots.add(slot);
      });
      return [champion.id, slots];
    }),
  );
}
const MELEE_SLOTS = loadMeleeSlots();
export function isMeleeAttackSlot(championId: ChampionId, slot: number): boolean {
  return MELEE_SLOTS.get(championId)?.has(slot) ?? false;
}
export class MeleeAttackAnimationTracker {
  private readonly until = new Map<EntityId, number>();
  ingest(state: WireSimState, nowMs: number): void {
    for (const event of state.meleeAttackEvents ?? [])
      this.until.set(event.caster, nowMs + MELEE_ATTACK_ANIMATION_MS);
    for (const event of state.castEvents) {
      const caster = state.entities.find((entity) => entity.entityId === event.caster);
      if (caster !== undefined && isMeleeAttackSlot(caster.championId, event.slot))
        this.until.set(event.caster, nowMs + MELEE_ATTACK_ANIMATION_MS);
    }
  }
  isAttacking(entityId: EntityId, nowMs: number): boolean {
    const until = this.until.get(entityId);
    if (until === undefined) return false;
    if (nowMs < until) return true;
    this.until.delete(entityId);
    return false;
  }
}
